import { useContext, useEffect, useMemo, useState } from 'react'

import {
  DueDateCardEmbed,
  DueDateCardEmbedProps,
  DueDateForm,
  DueDateFormProps,
} from '@hhgtech/hhg-components/components'
import { LOCALE_HAS_COMMUNITY_SITE } from '@hhgtech/hhg-components/constants'
import { useHealthToolsCache } from '@hhgtech/hhg-components/hooks'
import { getURLwithSSOTracking } from '@hhgtech/hhg-components/ssoV2'
import type { ReviewedUserApi } from '@hhgtech/hhg-components/together/interfaces/types'

import { callApiNew } from 'api/axios'
import { formatUserProfileUrl } from 'api/dataTransform'
import { PATHS } from 'api/paths'
import { redirectToSSOWithReturnUrl } from 'api/utils/redirects'
import { STORE_KEY } from 'components/templates/dueDateCalculator/utils'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { basePath } from 'interfaces/constants'
import { HEALTH_TOOL_TYPE } from 'interfaces/constants/healthTools'
import { DueDateInfo } from 'interfaces/types'
import { AppContext } from 'state/appContext'
import { configCdnImageSize } from 'utils/url'

import { DueDateInfoType } from '../due-date'
import { HealthToolCardEmbedProps } from '../types'

type CommonProps = HealthToolCardEmbedProps

export type DueDateCardWrapperProps =
  | ({ onlyUseForm: true } & Partial<DueDateFormProps> & CommonProps)
  | ({ onlyUseForm?: false } & Partial<DueDateCardEmbedProps> & CommonProps)

export const DueDateCardWrapper = ({
  onlyUseForm,
  isSide,
  showUserResult: _showUserResult,
  ...props
}: DueDateCardWrapperProps) => {
  const { locale } = useRouterExtend()
  const { getHealthToolLink } = useHealthToolsCache(locale)
  const dueDateLink = useMemo(
    () => getHealthToolLink(HEALTH_TOOL_TYPE.DUE_DATE),
    [getHealthToolLink],
  )
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()

  const { state } = useContext(AppContext) || {}
  const { userInfo } = state || {}
  const [dueDatePageInfo, setDueDatePageInfo] = useState<DueDateInfo>(
    {} as DueDateInfo,
  )

  const dueDatePageInfoResImg = useMemo(() => {
    return (
      dueDatePageInfo && {
        ...dueDatePageInfo,
        ['featured_image']: configCdnImageSize(
          dueDatePageInfo['featured_image'],
          { width: 48 },
        ),
        ['reviewed_by']: {
          ...dueDatePageInfo['reviewed_by'],
          avatar: configCdnImageSize(dueDatePageInfo['reviewed_by']?.avatar, {
            width: 32,
          }),
        },
      }
    )
  }, [dueDatePageInfo])

  const expertUrl = useMemo(
    () => formatUserProfileUrl(dueDatePageInfo?.reviewed_by as ReviewedUserApi),
    [dueDatePageInfo],
  )

  useEffect(() => {
    ;(async () => {
      const res = await callApiNew(PATHS.HEALTH_TOOL.GET_DUE_DATE_INFO)
      setDueDatePageInfo(res?.data)
    })()
  }, [])

  const onSubmitDueDate = (
    _: Omit<DueDateInfoType, 'startDate' | 'dueDate'>,
    redirectQuery?: string,
  ) => {
    const destination = `${
      window.location.origin
    }${basePath}${dueDateLink}?${redirectQuery}${
      LOCALE_HAS_COMMUNITY_SITE.includes(locale) ? '&joincommunity=1' : ''
    }`

    if (!userInfo?.id) {
      return redirectToSSOWithReturnUrl(destination, 'due-date')
    }
    const dueDateInfo = {
      source_url: window.location.href,
    }
    localStorage.setItem(STORE_KEY, JSON.stringify(dueDateInfo))
    window.location.href =
      getURLwithSSOTracking(destination, {
        source: 'due-date',
      })?.href || destination
  }
  if (!dueDateLink)
    return (
      <div
        style={{
          height: 500,
        }}
      />
    )

  return onlyUseForm ? (
    <DueDateForm
      isMobile={isMobile}
      onSubmit={onSubmitDueDate}
      formLayout={isMobile || isSide ? 'default' : 'inline'}
      submitProps={{
        dataEventLabel: currentUrl,
      }}
      {...props}
      locale={locale}
    />
  ) : (
    <DueDateCardEmbed
      dueDatePageInfo={dueDatePageInfoResImg}
      expertUrl={expertUrl}
      formProps={
        {
          isMobile,
          formLayout: isMobile || isSide ? 'default' : 'inline',
          onSubmit: onSubmitDueDate,
          submitProps: {
            dataEventLabel: currentUrl,
          },
        } as any
      }
      {...props}
      locale={locale}
    />
  )
}
